import { Box, Grid, GridItem } from '@everlywell/ui-kit';
import SectionContainer from 'components/PageSections/components/SectionContainer';
import SectionHeader, {
  SectionHeaderProps,
} from 'components/PageSections/components/SectionHeader';
import { navigate } from 'gatsby';
import React from 'react';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';

import Card, { CardProps } from './Card';
import CardCarousel from './CardCarousel';

// Types ----------

export interface HomepageHeroProps {
  title?: string | null;
  subtitle?: string | null;
  eyebrow?: string | null;
  desktopCards?: CardProps[] | null;
  mobileCards?: CardProps[] | null;
  backgroundColor?: string | null;
  autoScroll?: 'on' | 'off';
  displayMode?: 'default' | 'straight';
  titleTag?: string | null;
}

// HomepageHero ------

const HomepageHero: React.FC<HomepageHeroProps> = (props) => {
  const {
    title,
    subtitle,
    desktopCards,
    mobileCards,
    backgroundColor,
    autoScroll,
    titleTag,
    displayMode,
    eyebrow,
  } = props;

  const trackClick = (label?: string) => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        sectionHeader: title || 'No title provided',
        contentfulSection: 'HeroCarousel',
        ctaText: label || 'No CTA label',
      },
    });
  };

  const handleCardClick = (
    _event: React.MouseEvent<HTMLDivElement>,
    options: { url: string; label: string },
  ) => {
    const { label, url } = options;

    trackClick(label);

    if (url) navigate(url);
  };

  return (
    <SectionContainer
      size={['sm', 'ultra']}
      margin="0 auto"
      paddingTop={[8, null, 20]}
      paddingBottom={[10, null, 10]}
      overflow="hidden"
      backgroundColor={backgroundColor || undefined}
    >
      {(eyebrow || title || subtitle) && (
        <Box marginBottom={[10, 16]}>
          <SectionHeader
            eyebrow={eyebrow}
            title={title}
            subtitle={subtitle}
            headingLevel={
              (titleTag || 'h2') as SectionHeaderProps['headingLevel']
            }
            headingSize={eyebrow ? undefined : ['2xl', null, '3xl']}
          />
        </Box>
      )}

      {/* Mobile container */}
      <Grid
        data-testid="mobile-cards"
        spacing="2"
        display={['flex', null, 'none']}
      >
        {mobileCards &&
          mobileCards.map((card, index) => {
            // Render Link wrapper for cards with links
            if (card.cta && card.cta.url) {
              return (
                <GridItem width={[1 / 2]} key={`mobile-card-${index}`}>
                  <Box
                    onClick={(event) => {
                      handleCardClick(event, {
                        url: card.cta.url,
                        label: card.cta.label,
                      });
                    }}
                    cursor="pointer"
                    role="link"
                  >
                    <Card {...card} onClickCta={trackClick} />
                  </Box>
                </GridItem>
              );
            }

            return (
              <GridItem width={[1 / 2]} key={`mobile-card-${index}`}>
                <Card {...card} />
              </GridItem>
            );
          })}
      </Grid>

      {/* Desktop container */}
      <Box
        position="relative"
        width="1920px" // Fixed width to keep fade out gradients always at the same position
        height="auto"
        left="50%"
        transform="translate(-50%, 0)"
        display={['none', null, 'block']}
        data-testid="desktop-cards"
      >
        <Box
          display="block"
          width="420px"
          height="calc(100% + 1px)" // 1px off to prevent cards from showing up.
          position="absolute"
          left="-1px" // 1px off to prevent cards from showing up.
          top="0"
          zIndex={2}
          pointerEvents="none"
          // Gradient overlay extracted from Figma design directly.
          background="linear-gradient(270deg, rgba(255, 255, 255, 0.00) 12%, rgba(255, 255, 255, 0.70) 37%, #FFF 65%);"
        />

        <CardCarousel
          cards={
            desktopCards
              ? desktopCards?.map((card) => ({
                  ...card,
                  onClickCta: trackClick,
                }))
              : []
          }
          autoScroll={autoScroll}
          displayMode={displayMode}
        />

        <Box
          display="block"
          width="420px"
          height="calc(100% + 1px)" // 1px off to prevent cards from showing up.
          position="absolute"
          right="-1px" // 1px off to prevent cards from showing up.
          top="0"
          zIndex={2}
          pointerEvents="none"
          // Gradient overlay extracted from Figma design directly.
          background="linear-gradient(90deg, rgba(255, 255, 255, 0.00) 12%, rgba(255, 255, 255, 0.70) 37%, #FFF 65%);"
        />
      </Box>
    </SectionContainer>
  );
};

// Export ---------

export default React.memo(HomepageHero);
